<template>
    <div class="page-lobby"
         :style="{backgroundImage: `url(${backgroundImage})`}">
        <div class="page-lobby__content">
            <div class="lobby" ref="lobbyBox" id="lobbyBox"
                 :style="{backgroundImage: `url('${preloaderBackgroundImage}')`}"/>

            <div class="lobby-right" v-if="!isMobile">
                <div class="lobby-info">
                    <div class="lobby-info__nav" v-if="winnerListEnabled">
                        <v-btn :class="{active: tabsDialog === 1 || !winnerListEnabled}" @click="tabsDialog = 1">
                            {{ $t('front.game.titles.game_info') }}
                        </v-btn>
                        <v-btn :class="{active: tabsDialog === 2}" @click="tabsDialog = 2" v-if="winnerListEnabled">
                            {{ $t('front.game.titles.recent_winners') }}
                        </v-btn>
                    </div>
                    <div class="lobby-info__tabs">
                        <div class="tab" v-if="tabsDialog === 1 || !winnerListEnabled">
                            <picture>
                                <source :srcset="game.coverImage && game.coverImage.webp" type="image/webp">
                                <img ref="image" :src="game.coverImage && game.coverImage.jpg" :alt="game.title"/>
                            </picture>
                            <h1>{{ game.title }}</h1>
                            <h2>{{ game.platformTitle }}</h2>
                            <vue-custom-scrollbar class="scroll-area" :settings="scrollbarSettings">
                                <div v-html="gameDescription"></div>
                            </vue-custom-scrollbar>
                        </div>
                        <div class="tab winner-list" v-if="tabsDialog === 2 && winnerListEnabled">
                            <widget-winner :winners-per-view="9"/>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="page-lobby__suggested" v-if="relatedGames.length && !isMobile">
            <h2>{{ $t('front.game.titles.suggested_games') }}</h2>
            <div class="page-lobby__suggested-slider">
                <swiper ref="swiperSS" class="suggested-slider" v-bind="sliderSettings" :options="sliderSettings">
                    <swiper-slide v-for="(item, index) in relatedGames" :key="index" class="listGame__list-item">
                        <game-item :item="item"/>
                    </swiper-slide>
                </swiper>
                <div id="button-next-relacionados" class="swiper-button-next swiper-button-white"
                     @click="swiperNext()"></div>
                <div id="button-prev-relacionados" class="swiper-button-prev swiper-button-white"
                     @click="swiperPrev()"></div>
            </div>
        </div>

        <script v-html="jsonLdReviewType" type="application/ld+json" v-if="game"/>
    </div>
</template>

<script>
import {Swiper, SwiperSlide} from "vue-awesome-swiper";
import {appName, brandName, events, features} from "../../../../../config/app";
import {disableBackgroundScroll, enableBackgroundScroll} from '../../../../util/disableBackgroundScroll';
import vueCustomScrollbar from 'vue-custom-scrollbar';
import 'vue-custom-scrollbar/dist/vueScrollbar.css';
import {
    getGamePlayFunRedirectUrl,
    getGamePlayRealRedirectUrl,
    mustGameRedirect,
} from "../../../../util/game/launchGameManager";
import screenResolution from "../../../../mixins/screenResolution";
import {saveRecommendedGameLaunchAction} from "../../../../util/game/recommendedGame";
import ApiData from "../../../../mixins/apiData";
import {fetchLobbyRecommendedGames} from "../../../../api";
import {routesNames} from "../../../../interfaces/routeInterface";

export default {
    name: "GameLobby",

    mixins: [screenResolution, ApiData],

    components: {
        Swiper,
        SwiperSlide,
        vueCustomScrollbar,
    },

    data: function () {
        return {
            resizeInterval: undefined,
            updateIframeSizeInterval: undefined,
            gameAlias: undefined,
            playMode: this.$route.params.mode || 'real',
            sliderSettings: {
                init: true,
                loop: false,
                slidesPerView: 2,
                initialSlide: 0,
                direction: 'horizontal',
                spaceBetween: 0,
                speed: 500,
                navigation: {
                    nextEl: '#button-next-relacionados',
                    prevEl: '#button-prev-relacionados',
                },
                breakpoints: {
                    600: {
                        slidesPerView: 3,
                        spaceBetween: 0,
                    },
                    801: {
                        slidesPerView: 4,
                        spaceBetween: 0,
                    },
                    1025: {
                        slidesPerView: 5,
                        spaceBetween: 0,
                    },
                }
            },
            scrollbarSettings: {
                suppressScrollY: false,
                suppressScrollX: true,
                wheelPropagation: false,
            },
            tabsDialog: 2, // show recent winners instead of game info (for robots)
            bodyElement: undefined,
            bodyScrollY: 0,
            relatedGames: [],
        }
    },

    metaInfo() {
        return this.game && this.game.metaInfo;
    },

    beforeMount() {
        if (!this.game) {
            this.$router.push({
                name: routesNames.PAGE_NOT_FOUND,
            });
        }
    },

    beforeUpdate() {
        if (!this.game) {
            this.$router.push({
                name: routesNames.PAGE_NOT_FOUND,
            });
        }
    },

    mounted() {
        if (mustGameRedirect(this.game, this.isMobile)) {
            if (this.playMode === 'real') {
                if (this.user) {
                    getGamePlayRealRedirectUrl(this.game, this.$store)
                        .then(url => window.location.href = url)
                        .catch(() => window.location.href = '/');
                } else {
                    getGamePlayRealRedirectUrl(this.game, this.$store)
                        .then(url => {
                            this.$store.commit('SET_INTENDED_URL', {
                                url,
                                external: true,
                                target: 'self',
                                appendUser: true,
                                isLaunchGameUrl: true
                            });
                        })
                        .catch(() => window.location.href = '/');
                }
            } else {
                getGamePlayFunRedirectUrl(this.game, this.$store)
                    .then(url => window.location.href = url)
                    .catch(() => window.location.href = '/');
            }
        } else {
            this.isMobile && require('../../../../chat/index').hideChat(this);

            // store lobby box for update game size in GameTray
            this.$store.commit('SET_LOBBY_BOX', this.$refs.lobbyBox);

            // fetch game data and move game to lobby box
            this.mountGame();

            window.addEventListener('resize', this.setResizeInterval, false);
            window.addEventListener('scroll', this.setResizeInterval, false);
            window.addEventListener('touchmove', this.setResizeInterval, false);
            window.addEventListener('orientationchange', this.orientationChanged, false);
        }
    },

    updated() {
        // set new lobby box
        this.$store.commit('SET_LOBBY_BOX', this.$refs.lobbyBox);

        // route was updated. mount new game
        if (this.gameAlias !== this.$route.params.alias) {
            this.mountGame();
        }
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.setResizeInterval, false);
        window.removeEventListener('scroll', this.setResizeInterval, false);
        window.removeEventListener('touchmove', this.setResizeInterval, false);
        window.removeEventListener('orientationchange', this.orientationChanged, false);

        // clear all updating positions intervals
        this.clearAllIntervals();

        this.isMobile && require('../../../../chat/index').showChat(this);
    },

    destroyed() {
        // clear intervals once more. some browsers not triggered beforeDestroy
        this.clearAllIntervals();
    },

    watch: {
        user(user, oldUser) {
            if (user && !oldUser) {
                // if user passed to lobby via direct link, and he's not logged in and game mode is real,
                // game will not be mounted in mounted hook, login dialog will be opened instead
                // after user appears, calls fetchGameContent
                this.game && !this.mountedGame && this.fetchGameContent(this.game);

                // save launch game statistic
                saveRecommendedGameLaunchAction(this.$store, this.game, this.$route.query);
            }
        },

        // watch for game. game mounts in GameTray. when game whole prepared GameTray sets 'mounted' to the game
        // set flags to game and move it to lobby box
        mountedGame(mountedGame) {
            if (mountedGame) {
                !mountedGame.isActive && this.$store.commit('SET_LAUNCHED_GAME_IS_IN_LOBBY_BOX', this.gameAlias);

                this.moveGameToLobbyBox();
            }
        },

        isMobile(isMobile, oldIsMobile) {
            oldIsMobile && !isMobile && this.getRelatedGames();
        },
    },

    computed: {
        isMobile() {
            return this.$store.state.isDeviceMobile;
        },

        isHalloween() {
            return events.halloween;
        },

        isChristmas() {
            return events.christmas;
        },

        jsonLdReviewType() {
            return {
                "@context": "https://schema.org",
                "@type": "Review",
                "itemReviewed": {
                    "@type": "Game",
                    "name": this.game.title,
                    "image": this.game.image
                },
                "reviewRating": {
                    "@type": "Rating",
                    "ratingValue": this.game.rating,
                    "bestRating": 10,
                    "worstRating": 0
                },
                "author": {
                    "@type": "Person",
                    "name": appName
                },
                "reviewBody": this.$t('front.game.json_ld.review_body', {
                    gameTitle: this.game.title,
                    gamePlatform: this.game.platformTitle
                })
            };
        },

        swiper() {
            return this.$refs && this.$refs.swiperSS && this.$refs.swiperSS.$swiper;
        },

        preloaderBackgroundImage() {
            return `/public/${brandName}/spinners/spinner.gif`;
        },

        game() {
            return this.pageData && this.pageData.game;
        },

        user() {
            return this.$store.state.user;
        },

        locale() {
            return this.$store.state.locale;
        },

        userCurrency() {
            return this.user && this.user.currencyHtmlCode;
        },

        pageData() {
            return this.$store.getters.getPageData(this.$route);
        },

        gameTitle() {
            return this.game && this.game.title;
        },

        mountedGame() {
            return this.$store.getters.getMountedGameByAlias(this.gameAlias);
        },

        gameElement() {
            return this.mountedGame && this.mountedGame.element;
        },

        backgroundImage() {
            let webp = this.game && this.game.lobbyBackgroundImage && this.game.lobbyBackgroundImage.webp;

            if (this.isHalloween) {
                return '/public/halloween/lobby-background.jpg'
            }

            if (this.isChristmas && !webp) {
                return `/public/${brandName}/christmas/lobby-bg.jpg`
            }

            if (webp) {
                return this.game.lobbyBackgroundImage.webp;
            }

            return `/public/${brandName}/lobby/default_background_image.png`
        },

        gameDescription() {
            return this.game && this.game.details || '';
        },

        winnerListEnabled() {
            return features.lobbyWinnerList;
        },
    },

    methods: {
        swiperNext() {
            this.swiper && this.swiper.slideNext();
        },

        swiperPrev() {
            this.swiper && this.swiper.slidePrev();
        },

        mountGame() {
            this.gameAlias = this.$route.params.alias;

            if (this.$store.getters.getMountedGameByAlias(this.gameAlias)) {
                // game already mounted. set flags and move game to lobby box
                this.$store.commit('SET_LAUNCHED_GAME_IS_IN_LOBBY_BOX', this.gameAlias);
                this.moveGameToLobbyBox();
            } else {
                this.fetchGameContent();
            }

            !this.isMobile && this.getRelatedGames();

            // save launch game statistic
            this.user && saveRecommendedGameLaunchAction(this.$store, this.game, this.$route.query);
        },

        fetchGameContent() {
            // set game to array of launched games
            this.$store.commit('SET_LAUNCHED_GAME', {
                alias: this.gameAlias,
                game: this.game,
                isTray: false,
            })
        },

        moveGameToLobbyBox() {
            this.gameElement.style.position = 'absolute';
            this.gameElement.classList.add('active');

            this.setResizeInterval();
        },

        setResizeInterval() {
            this.clearAllIntervals();
            this.resizeInterval = setInterval(this.updateGamePosition, 200);
        },

        orientationChanged(event) {
            // clear all update game position intervals
            this.clearAllIntervals();

            // define orientation
            let orientation;

            let eventOrientation = event.target && event.target.screen && event.target.screen.orientation && event.target.screen.orientation.type;

            if (eventOrientation) {
                orientation = eventOrientation.includes('portrait') ? 'portrait' : 'landscape';
            } else {
                // for ios
                if (window.orientation !== undefined) {
                    orientation = window.orientation === 90 || window.orientation === -90 ? 'landscape' : 'portrait';
                } else {
                    // check old orientation before DOM is updated
                    orientation = window.innerHeight < window.innerWidth ? 'portrait' : 'landscape';
                }
            }

            // store orientation for update iframe in GameTray
            this.$store.commit('SET_DEVICE_ORIENTATION', orientation);

            // toggle to fullscreen on landscape and back
            this.toggleFullScreen(orientation);

            setTimeout(this.setResizeInterval, 200);
        },

        clearAllIntervals() {
            if (this.resizeInterval) {
                clearInterval(this.resizeInterval);
                this.resizeInterval = null;
            }

            if (this.updateIframeSizeInterval) {
                clearInterval(this.updateIframeSizeInterval);
                this.updateIframeSizeInterval = null;
            }
        },

        toggleFullScreen(orientation) {
            if (orientation === 'landscape') {
                this.gameElement.classList.add('full-screen');

                // set body and disable scroll
                this.bodyElement = this.bodyElement || document.querySelector('body');
                this.bodyScrollY = window.pageYOffset || 0;
                disableBackgroundScroll(this.bodyElement, this.bodyScrollY);
            } else {
                this.gameElement.classList.remove('full-screen');

                // enable scroll
                this.bodyElement = this.bodyElement || document.querySelector('body');
                this.bodyScrollY = window.pageYOffset || 0;
                enableBackgroundScroll(this.bodyElement, this.bodyScrollY);
            }
        },

        setUpdateIframeSizeInterval() {
            if (this.updateIframeSizeInterval) {
                clearInterval(this.updateIframeSizeInterval);
                this.updateIframeSizeInterval = null;
            }

            this.updateIframeSizeInterval = setInterval(this.updateIframeSize, 200);
        },

        updateGamePosition() {
            if (this.gameElement.classList.contains('full-screen')) {
                // game is in correct position. stop interval
                clearInterval(this.resizeInterval);
                this.resizeInterval = null;

                setTimeout(this.setUpdateIframeSizeInterval, 300);
            } else {
                let gameBox = this.gameElement.getBoundingClientRect();
                let lobbyBox = this.$store.state.lobbyBox.getBoundingClientRect();

                // set game to lobby box
                this.gameElement.style.left = lobbyBox.left + 'px';
                this.gameElement.style.top = lobbyBox.top + window.scrollY + 'px';
                this.gameElement.style.width = lobbyBox.width + 'px';

                if (window.innerHeight < lobbyBox.height) {
                    // reduce game height by header height (64px) for fit game in screen by height
                    this.gameElement.style.height = window.innerHeight - 64 + 'px';
                } else {
                    this.gameElement.style.height = lobbyBox.height + 'px';
                }

                // game is in correct position. stop interval
                if (parseInt(lobbyBox.left) === parseInt(gameBox.left) &&
                    parseInt(lobbyBox.width) === parseInt(gameBox.width)) {
                    clearInterval(this.resizeInterval);
                    this.resizeInterval = null;

                    setTimeout(this.setUpdateIframeSizeInterval, 300);
                }
            }
        },

        updateIframeSize() {
            let iframeWrapperHeight = this.mountedGame.iframeWrapper.clientHeight;
            let iframeWrapperWidth = this.mountedGame.iframeWrapper.clientWidth;

            if (!(iframeWrapperWidth && iframeWrapperHeight)) {
                return;
            }

            if (this.gameElement.classList.contains('full-screen')) {
                // define iframe and its wrapper proportions and fit iframe
                let iframeWrapperProportion = iframeWrapperWidth / iframeWrapperHeight;
                let gameProportion = this.mountedGame.proportion;

                if (iframeWrapperProportion > gameProportion) {
                    // fit iframe as 'pillar box'
                    let iframeWidth = Math.floor(iframeWrapperHeight * gameProportion);
                    let iframeLeft = Math.floor((iframeWrapperWidth - iframeWidth) / 2);

                    this.mountedGame.iframe.style.height = iframeWrapperHeight + 'px';
                    this.mountedGame.iframe.style.width = iframeWidth + 'px';
                    this.mountedGame.iframe.style.left = iframeLeft + 'px';
                    this.mountedGame.iframe.style.top = '0px';
                } else {
                    // fit iframe as 'letter box'
                    let iframeHeight = Math.floor(iframeWrapperWidth / gameProportion);
                    let iframeTop = Math.floor((iframeWrapperHeight - iframeHeight) / 2);

                    // align iframe to top when vertical padding is low
                    if (iframeTop < 10) {
                        iframeTop = 0;
                    }

                    this.mountedGame.iframe.style.width = iframeWrapperWidth + 'px';
                    this.mountedGame.iframe.style.height = iframeHeight + 'px';
                    this.mountedGame.iframe.style.top = iframeTop + 'px';
                    this.mountedGame.iframe.style.left = '0px';
                }
            } else {
                // cover iframe wrapper with iframe
                this.mountedGame.iframe.style.width = iframeWrapperWidth + 'px';
                this.mountedGame.iframe.style.height = iframeWrapperHeight + 'px';
                this.mountedGame.iframe.style.top = '0px';
                this.mountedGame.iframe.style.left = '0px';
            }

            let iframeHeight = this.mountedGame.iframe.clientHeight;
            let iframeWidth = this.mountedGame.iframe.clientWidth;

            if (iframeHeight === iframeWrapperHeight || iframeWidth === iframeWrapperWidth) {
                // iframe is in correct position. stop interval
                clearInterval(this.updateIframeSizeInterval);
                this.updateIframeSizeInterval = null;

                // make game visible one time after mounted
                if (this.gameElement.classList.contains('start')) {
                    this.gameElement.classList.remove('start');
                }
            }
        },

        getRelatedGames() {
            fetchLobbyRecommendedGames(this.createApiData({
                gameId: this.game && this.game.id,
                count: 10,
            }))
                .then(relatedGames => {
                    this.relatedGames = relatedGames;
                })
                .catch(error => console.log(error));
        },
    },
}
</script>

<style lang="sass">
@import "sass/components/pages/parts/target/GameLobby"
</style>
